import React, {useState, useRef, useEffect} from 'react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {FormattedMessage} from 'react-intl'
import TicketCard from './repair-tracking-tool-item'
import {
    Stack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Spinner,
    Flex,
    Button,
    Text,
    useMultiStyleConfig,
    useBreakpointValue,
} from '@chakra-ui/react'
import routingObject from '../../utils/static-routes'
import LoadingSpinner from '../../components/loading-spinner'
import useProductRegistrationHandler from '../../custom-hooks/useProductRegistrationHandler'
import {useTokenHelper} from '../../utils/token-helper'

const formatDate = (dateString) => {
    if (!dateString) return ''

    const [year, month, day] = dateString.split('-')
    return `${day}/${month}/${year}`
}

const RepairTracking = () => {
    const styles = useMultiStyleConfig('RepairTrackingTool')
    const headingRef = useRef()
    const {data: customer} = useCurrentCustomer()
    const [tickets, setTickets] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const appOrigin = useOrigin(false)
    const isMobile = useBreakpointValue({base: true, md: false})
    const handleProductRegistrationClick = useProductRegistrationHandler(routingObject)
    const {fetchSAPCPIValidToken} = useTokenHelper()

    useEffect(() => {
        const fetchRepairTickets = async () => {
            if (!customer.c_gigyaId) return
            setLoading(true)
            setError(null)
            try {
                const accessToken = await fetchSAPCPIValidToken()
                const queryParams = new URLSearchParams()
                if (customer.c_gigyaId) queryParams.append('customerId', customer.c_gigyaId)
                if (customer.email) queryParams.append('emailAddress', customer.email)
                const response = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-repair-tickets-sap-cpi?${queryParams.toString()}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${accessToken}`,
                        },
                    },
                )

                const data = await response.json()
                if (data.success) {
                    setTickets(data.claims)
                } else {
                    setError(data.error || 'Failed to fetch tickets')
                }
            } catch (err) {
                setError('An error occurred while fetching tickets')
            } finally {
                setLoading(false)
            }
        }

        fetchRepairTickets()
    }, [customer?.c_gigyaId])

    useEffect(() => {
        // Focus the Title header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    return (
        <Stack
            data-testid="account-repair-tracking-tool-page"
            position={'relative'}
            spacing={{base: 6, lg: 10}}
        >
            <Text as="h1" tabIndex="0" ref={headingRef} {...styles.headingTitle}>
                <FormattedMessage defaultMessage="Il tuo account" id="account.heading.account" />
            </Text>
            {loading && <LoadingSpinner />}
            {!loading && tickets.length <= 0 && (
                <Flex {...styles.productRegistrationBox}>
                    <Flex direction="column" gap={6}>
                        <Text as="h1" {...styles.headingTitleBox}>
                            <FormattedMessage
                                defaultMessage="Repair tracking tool"
                                id="account.heading.repair_tracking_tool"
                            />
                        </Text>
                        <Text {...styles.description}>
                            <FormattedMessage
                                id="repair_tool.label.description"
                                defaultMessage="Below you can check the tickets opened by our repair center and track the
                            status of the product for which you have requested a repair."
                            />
                        </Text>
                        {!error && (
                            <Text {...styles.description}>
                                <FormattedMessage
                                    id="repair_tool.label.no_ticket"
                                    defaultMessage="You have no open tickets."
                                />
                            </Text>
                        )}
                        {error && (
                            <Text color="red.500" {...styles.description}>
                                {`Error: ${error}`}
                            </Text>
                        )}
                        <Button
                            {...styles.registerButton}
                            onClick={() => handleProductRegistrationClick()}
                            variant="primary"
                            data-testid="button"
                        >
                            <FormattedMessage
                                id="repair-tracking.button"
                                defaultMessage="Contact us"
                            />
                        </Button>
                    </Flex>
                </Flex>
            )}
            {!loading && tickets.length > 0 && (
                <Stack gap={6}>
                    <Text as="h1" {...styles.headingTitleBox}>
                        <FormattedMessage
                            defaultMessage="Repair tracking tool"
                            id="account.heading.repair_tracking_tool"
                        />
                    </Text>
                    <Text {...styles.description}>
                        <FormattedMessage
                            id="repair_tool.label.description"
                            defaultMessage="Below you can check the tickets opened by our repair center and track the
                        status of the product for which you have requested a repair."
                        />
                    </Text>
                    {!isMobile && (
                        <TableContainer {...styles.tableContainer}>
                            <Table>
                                <Thead>
                                    <Tr {...styles.tableHeader}>
                                        <Th fontWeight="bold" color="black">
                                            <FormattedMessage
                                                id="repair_tool.label.ticket"
                                                defaultMessage="Ticket:"
                                            />
                                        </Th>
                                        <Th fontWeight="bold" color="black">
                                            <FormattedMessage
                                                id="repair_tool.label.entry_date"
                                                defaultMessage="Entry Date:"
                                            />
                                        </Th>
                                        <Th fontWeight="bold" color="black">
                                            <FormattedMessage
                                                id="repair_tool.label.product"
                                                defaultMessage="Product:"
                                            />
                                        </Th>
                                        <Th fontWeight="bold" color="black">
                                            <FormattedMessage
                                                id="repair_tool.label.shipping_date"
                                                defaultMessage="Repair Shipping Date:"
                                            />
                                        </Th>
                                        <Th fontWeight="bold" color="black">
                                            <FormattedMessage
                                                id="repair_tool.label.status"
                                                defaultMessage="Status:"
                                            />
                                        </Th>
                                    </Tr>
                                </Thead>

                                <Tbody {...styles.tableBody}>
                                    {loading ? (
                                        <Tr>
                                            <Td colSpan="5" textAlign="center">
                                                <Spinner size="lg" />
                                            </Td>
                                        </Tr>
                                    ) : tickets.length === 0 ? (
                                        <Tr>
                                            <Td
                                                colSpan="5"
                                                textAlign="center"
                                                fontWeight="bold"
                                                color="gray.500"
                                            >
                                                <FormattedMessage
                                                    id="repair_tool.label.no_ticket"
                                                    defaultMessage="You not have open ticket."
                                                />
                                            </Td>
                                        </Tr>
                                    ) : (
                                        tickets.map((ticket) => (
                                            <Tr key={ticket.claimId} {...styles.tableRow}>
                                                <Td>{ticket.claimId}</Td>
                                                <Td>{formatDate(ticket.entryDate)}</Td>
                                                <Td>
                                                    {ticket.productDescription} <br /> ({ticket.sku}
                                                    )
                                                </Td>
                                                <Td>{formatDate(ticket.exitDate)}</Td>
                                                <Td
                                                    color={
                                                        ticket.status.toUpperCase() === 'DELIVERED'
                                                            ? 'text.success'
                                                            : 'red.500'
                                                    }
                                                    fontWeight="bold"
                                                >
                                                    {ticket.status.toUpperCase()}
                                                </Td>
                                            </Tr>
                                        ))
                                    )}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    )}
                    {isMobile && (
                        <>
                            {tickets.map((ticket) => (
                                <TicketCard key={ticket.claimId} ticket={ticket} />
                            ))}
                        </>
                    )}
                </Stack>
            )}
        </Stack>
    )
}

RepairTracking.getTemplateName = () => 'repair-tracking-tool'

export default RepairTracking
